import {
  NJButton,
  NJHeading,
  NJInlineMessage,
  NJSelectItem,
  NJSelectRoot,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { ConfirmModal } from "@/components/ConfirmModal";
import { StatusBadge } from "@/components/StatusBadge";
import { MetaTagList } from "@/components/store/MetaTagList";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import { SkillCompatibilityTags } from "@/features/skills/SkillCompatibilityTags";
import {
  useAdminSkill,
  useDeleteAdminSkill,
  useUpdateAdminSkill,
  useUploadAdminSkillVersion,
} from "./useAdminSkills";

/** Admin skill detail — review a `PENDING` proposal, edit, manage versions, delete. */
export function AdminSkillDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: skill, isLoading, isError } = useAdminSkill(slug);
  const updateSkill = useUpdateAdminSkill();
  const deleteSkill = useDeleteAdminSkill();
  const uploadVersion = useUploadAdminSkillVersion();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [mode, setMode] = useState<"NEW_VERSION" | "REPLACE_CURRENT">(
    "NEW_VERSION",
  );
  const [zipFile, setZipFile] = useState<File | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <NJSpinner aria-label="Loading the skill" />
      </div>
    );
  }

  if (isError || !skill) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton to="/admin/skills" label="Back to Skills" />
        <NJInlineMessage variant="error">
          Could not load this skill.
        </NJInlineMessage>
      </div>
    );
  }

  const isGithub = skill.sourceType === "GITHUB";

  return (
    <div className="flex flex-col gap-6">
      <BackButton to="/admin/skills" label="Back to Skills" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <NJHeading as="h1" scale="lg">
            {skill.name}
          </NJHeading>
          <StatusBadge status={skill.status} />
        </div>
        <div className="flex gap-2">
          <NJButton
            label="Edit"
            icon="edit"
            onClick={() => navigate(`/admin/skills/${skill.slug}/edit`)}
          />
          <NJButton
            label="Delete skill"
            variant="destructive"
            emphasis="subtle"
            disabled={deleteSkill.isPending}
            onClick={() => setConfirmOpen(true)}
          />
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete skill"
        isPending={deleteSkill.isPending}
        onConfirm={() => {
          if (slug) {
            deleteSkill.mutate(slug, {
              onSuccess: () => navigate("/admin/skills"),
            });
          }
        }}
      >
        Delete skill <strong>{skill.name}</strong>? This action cannot be
        undone.
      </ConfirmModal>

      <section className="grid gap-4 rounded-xl border border-nj-neutral-subtle-default p-6 sm:grid-cols-2">
        <Field label="Slug" value={skill.slug} />
        <Field label="Source" value={skill.sourceType} />
        <Field label="Created by" value={skill.createdByName || "-"} />
        <Field
          label="Current version"
          value={skill.currentVersion || "No version yet"}
        />
        {isGithub && skill.repositoryUrl && (
          <div className="sm:col-span-2">
            <Field label="Repository" value={skill.repositoryUrl} />
          </div>
        )}
        {skill.description && (
          <div className="sm:col-span-2">
            <Field label="Short description" value={skill.description} />
          </div>
        )}
      </section>

      <div className="grid gap-4 rounded-xl border border-nj-neutral-subtle-default p-6 sm:grid-cols-2">
        <MetaTagList label="Categories" items={skill.includedSkills ?? []} />
        <div className="flex flex-col gap-1.5">
          <NJText
            as="span"
            scale="xs"
            className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
          >
            Compatible harnesses
          </NJText>
          <SkillCompatibilityTags items={skill.compatibleHarnesses ?? []} />
        </div>
      </div>

      {skill.fullDescription && (
        <section className="flex flex-col gap-2 rounded-xl border border-nj-neutral-subtle-default p-6">
          <NJHeading as="h2" scale="sm">
            Full description
          </NJHeading>
          <MarkdownMessage content={skill.fullDescription} />
        </section>
      )}

      <section className="flex max-w-md flex-col gap-3 rounded-xl border border-nj-neutral-subtle-default p-6">
        <NJHeading as="h2" scale="sm">
          Validation
        </NJHeading>
        <NJSelectRoot
          id="admin-skill-status"
          label="Status"
          value={skill.status}
          listNavigationLabel="Use the arrows and Enter to choose the status"
          buttonDefaultValueLabel="Choose a status"
          onChange={(value) => {
            if (value && slug) {
              updateSkill.mutate({ slug, patch: { status: value } });
            }
          }}
        >
          <NJSelectItem value="PENDING">Pending validation</NJSelectItem>
          <NJSelectItem value="ACTIVE">Active</NJSelectItem>
          <NJSelectItem value="DISABLED">Disabled</NJSelectItem>
        </NJSelectRoot>
        {updateSkill.isError && (
          <NJInlineMessage variant="error">
            Could not update the status.
          </NJInlineMessage>
        )}
      </section>

      {!isGithub && (
        <section className="flex flex-col gap-3 rounded-xl border border-nj-neutral-subtle-default p-6">
          <NJHeading as="h2" scale="sm">
            Versions
          </NJHeading>
          <ul className="flex flex-col divide-y divide-nj-neutral-subtle-default text-sm">
            {(skill.versions ?? []).map((version) => (
              <li key={version.version} className="py-2">
                v{version.version} — {version.fileName} (
                {version.isCurrent ? "current" : "archived"})
              </li>
            ))}
          </ul>

          <div className="grid max-w-lg gap-3">
            <NJSelectRoot
              id="admin-skill-version-mode"
              label="Mode"
              value={mode}
              listNavigationLabel="Use the arrows and Enter to choose the mode"
              buttonDefaultValueLabel="Choose a mode"
              onChange={(value) =>
                setMode((value ?? "NEW_VERSION") as typeof mode)
              }
            >
              <NJSelectItem value="NEW_VERSION">New version</NJSelectItem>
              <NJSelectItem value="REPLACE_CURRENT">
                Replace the current version
              </NJSelectItem>
            </NJSelectRoot>
            <input
              type="file"
              accept=".zip"
              onChange={(event) => setZipFile(event.target.files?.[0] ?? null)}
            />
            {uploadVersion.isError && (
              <NJInlineMessage variant="error">
                Could not upload this file.
              </NJInlineMessage>
            )}
            <NJButton
              label="Upload"
              disabled={!zipFile || uploadVersion.isPending}
              onClick={() => {
                if (slug && zipFile) {
                  uploadVersion.mutate(
                    { slug, mode, zipFile },
                    { onSuccess: () => setZipFile(null) },
                  );
                }
              }}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <NJText
        as="span"
        scale="xs"
        className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
      >
        {label}
      </NJText>
      <NJText as="span" scale="md" className="break-words">
        {value}
      </NJText>
    </div>
  );
}
